import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, X, Upload, Save, Shirt, Layers } from 'lucide-react';

function filenameToTitle(filename) {
  return filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
const STARTER_CHART_ROWS = ['Body Length', 'Chest Width (Laid Flat)', 'Sleeve Length (From Center Back)'];

const CATEGORIES = [
  { id: 'short-sleeve', label: 'Short Sleeve' },
  { id: 'long-sleeve', label: 'Long Sleeve' },
  { id: 'new-designs', label: 'New Designs' },
  { id: 'caps', label: 'Headwear' },
  { id: 'hoodies', label: 'Hoodies' },
  { id: 'tank-tops', label: 'Tank Tops' }
];

const GENDERS = [
  { id: 'men', label: 'Men' },
  { id: 'women', label: 'Women' },
  { id: 'kids', label: 'Kids' },
  { id: 'unisex', label: 'Unisex' }
];

function AdminTees({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Batch upload state
  const [batchMode, setBatchMode] = useState(false);
  const [batchItems, setBatchItems] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);

  const emptyForm = {
    title: '',
    category: 'long-sleeve',
    gender: 'men',
    subtitle: '',
    description: '',
    features: [],
    image_url: '',
    additional_images: [],
    base_price: 28,
    price_2xl: 29.50,
    price_3xl: 30.50,
    sizes: DEFAULT_SIZES,
    active: true,
    stock_by_size: {},
    on_sale: false,
    sale_price: '',
    colors: [],
    size_chart: []
  };

  const [formData, setFormData] = useState(emptyForm);
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/tees-products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('folder', 'tees');

    try {
      const response = await axios.post('/upload/image', uploadData);
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('folder', 'tees');

    try {
      const response = await axios.post('/upload/image', uploadData);
      setFormData(prev => ({
        ...prev,
        additional_images: [...(prev.additional_images || []), response.data.url]
      }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAdditionalImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.image_url) {
      toast.error('Please fill in title and upload an image');
      return;
    }

    if (formData.on_sale && formData.sale_price === '') {
      toast.error('Enter a sale price or turn off On Sale');
      return;
    }

    const colors = (formData.colors || [])
      .map(c => ({ name: (c.name || '').trim(), hex: c.hex || '#cccccc' }))
      .filter(c => c.name);

    // Drop unlabeled rows and blank cells so the chart only shows what was filled in
    const size_chart = (formData.size_chart || [])
      .map(r => ({
        label: (r.label || '').trim(),
        values: Object.fromEntries(
          Object.entries(r.values || {})
            .map(([size, v]) => [size, String(v).trim()])
            .filter(([size, v]) => v && formData.sizes.includes(size))
        )
      }))
      .filter(r => r.label);

    const payload = {
      ...formData,
      colors,
      size_chart,
      sale_price: formData.on_sale && formData.sale_price !== '' ? parseFloat(formData.sale_price) : null
    };

    try {
      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/admin/tees-products/${editingProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/tees-products`, payload);
        toast.success('Product created');
      }
      closeModal();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/tees-products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      category: product.category || 'long-sleeve',
      gender: product.gender || 'men',
      subtitle: product.subtitle || '',
      description: product.description || '',
      features: product.features || [],
      image_url: product.image_url || '',
      additional_images: product.additional_images || [],
      base_price: product.base_price || product.price || 28,
      price_2xl: product.price_2xl || 29.50,
      price_3xl: product.price_3xl || 30.50,
      sizes: product.sizes || DEFAULT_SIZES,
      active: product.active !== false,
      stock_by_size: product.stock_by_size || {},
      on_sale: product.on_sale || false,
      sale_price: product.sale_price != null ? product.sale_price : '',
      colors: product.colors || [],
      size_chart: product.size_chart || []
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData(emptyForm);
    setNewFeature('');
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addColor = () => {
    setFormData(prev => ({ ...prev, colors: [...(prev.colors || []), { name: '', hex: '#cccccc' }] }));
  };

  const updateColor = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const removeColor = (index) => {
    setFormData(prev => ({ ...prev, colors: prev.colors.filter((_, i) => i !== index) }));
  };

  const toggleSize = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : DEFAULT_SIZES.filter(s => s === size || prev.sizes.includes(s))
    }));
  };

  const addChartRow = () => {
    setFormData(prev => ({ ...prev, size_chart: [...(prev.size_chart || []), { label: '', values: {} }] }));
  };

  const updateChartRow = (index, changes) => {
    setFormData(prev => ({
      ...prev,
      size_chart: prev.size_chart.map((r, i) => i === index ? { ...r, ...changes } : r)
    }));
  };

  const removeChartRow = (index) => {
    setFormData(prev => ({ ...prev, size_chart: prev.size_chart.filter((_, i) => i !== index) }));
  };

  const copyChartFrom = (productId) => {
    const source = products.find(p => p.id === productId);
    if (!source?.size_chart?.length) return;
    setFormData(prev => ({
      ...prev,
      size_chart: source.size_chart.map(r => ({ label: r.label, values: { ...(r.values || {}) } }))
    }));
    toast.success('Chart copied — save to keep it');
  };

  const handleBatchFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setBatchUploading(true);
    const formDataUpload = new FormData();
    files.forEach(f => formDataUpload.append('files', f));

    try {
      const response = await axios.post('/upload/multiple', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newItems = response.data.uploaded.map(({ filename, url }) => ({
        _id: crypto.randomUUID(),
        url,
        title: filenameToTitle(filename),
        category: 'long-sleeve',
        gender: 'men'
      }));

      setBatchItems(prev => [...prev, ...newItems]);

      if (response.data.errors?.length) {
        toast.error(`${response.data.errors.length} file(s) failed to upload`);
      } else {
        toast.success(`${newItems.length} product(s) uploaded`);
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setBatchUploading(false);
      e.target.value = '';
    }
  };

  const updateBatchItem = (id, field, value) => {
    setBatchItems(prev => prev.map(item => item._id === id ? { ...item, [field]: value } : item));
  };

  const removeBatchItem = (id) => {
    setBatchItems(prev => prev.filter(item => item._id !== id));
  };

  const handleBatchSave = async () => {
    const untitled = batchItems.filter(item => !item.title.trim());
    if (untitled.length) {
      toast.error('Please add a title to every product before saving');
      return;
    }

    setBatchSaving(true);
    let saved = 0;
    let failed = 0;

    for (const item of batchItems) {
      try {
        await axios.post(`${BACKEND_URL}/api/admin/tees-products`, {
          title: item.title.trim(),
          category: item.category,
          gender: item.gender,
          subtitle: '',
          description: '',
          features: [],
          image_url: item.url,
          additional_images: [],
          base_price: 28,
          price_2xl: 29.50,
          price_3xl: 30.50,
          sizes: DEFAULT_SIZES,
          active: false,
          stock_by_size: {}
        });
        saved++;
      } catch {
        failed++;
      }
    }

    setBatchSaving(false);

    if (saved > 0) {
      toast.success(`${saved} product(s) saved as inactive — activate them from the grid`);
      setBatchItems([]);
      setBatchMode(false);
      fetchProducts();
    }
    if (failed > 0) {
      toast.error(`${failed} product(s) failed to save`);
    }
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="tees">
      <div className="admin-content">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <div>
            <h1 style={{ 
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '28px',
              margin: 0
            }}>
              Beach Bum Tees
            </h1>
            <p style={{ color: '#666', margin: '5px 0 0' }}>
              Manage tie-dyed apparel with size-based pricing
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="outline"
              onClick={() => setBatchMode(m => !m)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Layers size={16} />
              {batchMode ? 'Cancel Batch' : 'Batch Upload'}
            </Button>
            <Button
              onClick={() => setShowModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Plus size={18} />
              Add Product
            </Button>
          </div>
        </div>

        {/* ===== BATCH UPLOAD PANEL ===== */}
        {batchMode && (
          <div style={{ marginTop: '24px' }}>
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              border: '2px dashed #aaa', borderRadius: '12px', padding: '40px 20px',
              cursor: batchUploading ? 'not-allowed' : 'pointer',
              background: batchUploading ? '#f5f5f5' : '#fafafa',
              marginBottom: '24px'
            }}>
              <Upload size={36} color="#888" style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#444' }}>
                {batchUploading ? 'Uploading…' : 'Click to select product images'}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#888' }}>
                Select multiple files at once — they'll all upload together
              </p>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleBatchFileSelect}
                disabled={batchUploading}
              />
            </label>

            {batchItems.length > 0 && (
              <>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  {batchItems.length} product{batchItems.length !== 1 ? 's' : ''} ready — fill in details then save. All will be saved as <strong>inactive</strong> until you activate them.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: '20px',
                  marginBottom: '24px'
                }}>
                  {batchItems.map(item => (
                    <div key={item._id} style={{
                      background: '#fff', borderRadius: '12px', overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'relative'
                    }}>
                      <button
                        onClick={() => removeBatchItem(item._id)}
                        style={{
                          position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                          background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none',
                          borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <X size={14} />
                      </button>
                      <img src={item.url} alt={item.title} style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} />
                      <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Title *</label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={e => updateBatchItem(item._id, 'title', e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Category</label>
                          <select
                            value={item.category}
                            onChange={e => updateBatchItem(item._id, 'category', e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                          >
                            {CATEGORIES.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Gender</label>
                          <select
                            value={item.gender}
                            onChange={e => updateBatchItem(item._id, 'gender', e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                          >
                            {GENDERS.map(g => (
                              <option key={g.id} value={g.id}>{g.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <Button variant="outline" onClick={() => { setBatchItems([]); setBatchMode(false); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleBatchSave} disabled={batchSaving} style={{ minWidth: '180px' }}>
                    {batchSaving ? 'Saving…' : `Save ${batchItems.length} Product${batchItems.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner" />
          </div>
        ) : products.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: '#f8f9fa',
            borderRadius: '12px'
          }}>
            <Shirt size={48} style={{ color: '#ccc', marginBottom: '15px' }} />
            <p style={{ color: '#666', marginBottom: '20px' }}>
              No products yet. Add your first tee!
            </p>
            <Button onClick={() => setShowModal(true)}>
              <Plus size={18} style={{ marginRight: '8px' }} />
              Add Product
            </Button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {products.map(product => (
              <div 
                key={product.id}
                style={{
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  opacity: product.active ? 1 : 0.6
                }}
              >
                <div style={{
                  aspectRatio: '1',
                  overflow: 'hidden',
                  background: '#f5f5f5'
                }}>
                  <img 
                    src={product.image_url} 
                    alt={product.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ padding: '15px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>{product.title}</h3>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {product.on_sale && product.sale_price != null && (
                        <span style={{
                          background: '#fee2e2',
                          color: '#c0392b',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          SALE
                        </span>
                      )}
                      <span style={{
                        background: product.active ? '#dcfce7' : '#fee2e2',
                        color: product.active ? '#166534' : '#991b1b',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <p style={{
                    fontSize: '12px',
                    color: '#666',
                    margin: '0 0 8px'
                  }}>
                    {CATEGORIES.find(c => c.id === product.category)?.label || product.category || 'Uncategorized'}
                    {product.gender && ` · ${GENDERS.find(g => g.id === product.gender)?.label || product.gender}`}
                    {!product.gender && ' · No gender set'}
                  </p>
                  {product.on_sale && product.sale_price != null ? (
                    <p style={{ margin: '0 0 5px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#c0392b' }}>${product.sale_price}</span>
                      <span style={{ fontSize: '13px', color: '#999', textDecoration: 'line-through' }}>${product.base_price || product.price}</span>
                    </p>
                  ) : (
                    <p style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1a1a1a',
                      margin: '0 0 5px'
                    }}>
                      ${product.base_price || product.price}
                    </p>
                  )}
                  <p style={{
                    fontSize: '12px',
                    color: '#888',
                    margin: '0 0 5px'
                  }}>
                    2XL: ${product.price_2xl || (product.base_price || 28) + 1.50} • 3XL: ${product.price_3xl || (product.base_price || 28) + 2.50}
                  </p>
                  {product.stock_by_size && Object.keys(product.stock_by_size).length > 0 ? (
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 10px' }}>
                      Stock: {Object.entries(product.stock_by_size).map(([s, n]) => `${s}:${n}`).join(' · ')}
                    </p>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#aaa', margin: '0 0 10px' }}>Stock: Unlimited</p>
                  )}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(product)}
                      style={{ flex: 1 }}
                    >
                      <Edit size={14} style={{ marginRight: '5px' }} />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(product.id)}
                      style={{ color: '#dc2626', borderColor: '#dc2626' }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 style={{ margin: 0, fontSize: '20px' }}>
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h2>
                <button 
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px'
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
                {/* Image Upload */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Product Image *
                  </label>
                  {formData.image_url ? (
                    <div style={{ position: 'relative', marginBottom: '10px' }}>
                      <img 
                        src={formData.image_url} 
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '250px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          background: 'rgba(0,0,0,0.7)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '150px',
                      border: '2px dashed #ccc',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: '#f8f9fa'
                    }}>
                      <Upload size={32} color="#888" />
                      <span style={{ marginTop: '10px', color: '#666' }}>
                        {uploading ? 'Uploading...' : 'Click to upload image'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>

                {/* Additional Images */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Additional Images (e.g., back of shirt)
                  </label>
                  {(formData.additional_images || []).length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {formData.additional_images.map((url, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img
                            src={url}
                            alt={`Additional ${idx + 1}`}
                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                          />
                          <button
                            type="button"
                            onClick={() => removeAdditionalImage(idx)}
                            style={{
                              position: 'absolute', top: '-8px', right: '-8px',
                              background: '#dc2626', color: '#fff', border: 'none',
                              borderRadius: '50%', width: '20px', height: '20px',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
                            }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 14px', border: '1px dashed #ccc', borderRadius: '8px',
                    cursor: uploading ? 'not-allowed' : 'pointer', background: '#f8f9fa', color: '#555', fontSize: '13px'
                  }}>
                    <Upload size={16} />
                    {uploading ? 'Uploading...' : 'Add image'}
                    <input type="file" accept="image/*" onChange={handleAdditionalImageUpload} style={{ display: 'none' }} disabled={uploading} />
                  </label>
                </div>

                {/* Title */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., OBXBB Milky Way Long Sleeve"
                  />
                </div>

                {/* Category & Gender */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d0d0d0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: '#fff'
                      }}
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Gender *
                    </label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d0d0d0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: '#fff'
                      }}
                    >
                      {GENDERS.map(g => (
                        <option key={g.id} value={g.id}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Subtitle */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Subtitle (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., Treat yourself to one of Mrs.Bum's tie-dyed cotton shirts!"
                  />
                </div>

                {/* Description */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                    placeholder="We love these long sleeved cotton tees by SoftShirts..."
                  />
                </div>

                {/* Features */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Features (Bullet Points)
                  </label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        border: '1px solid #d0d0d0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      placeholder="e.g., 100% Combed Ringspun Cotton"
                    />
                    <Button type="button" variant="outline" onClick={addFeature}>
                      Add
                    </Button>
                  </div>
                  {formData.features.length > 0 && (
                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '10px'
                    }}>
                      {formData.features.map((feature, idx) => (
                        <li key={idx} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '5px 0',
                          fontSize: '14px'
                        }}>
                          <span>• {feature}</span>
                          <button
                            type="button"
                            onClick={() => removeFeature(idx)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#dc2626'
                            }}
                          >
                            <X size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Pricing */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr 1fr', 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Base Price (S-XL) *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666'
                      }}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.base_price}
                        onChange={(e) => setFormData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px 10px 25px',
                          border: '1px solid #d0d0d0',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      2XL Price
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666'
                      }}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price_2xl}
                        onChange={(e) => setFormData(prev => ({ ...prev, price_2xl: parseFloat(e.target.value) || 0 }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px 10px 25px',
                          border: '1px solid #d0d0d0',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      3XL Price
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666'
                      }}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price_3xl}
                        onChange={(e) => setFormData(prev => ({ ...prev, price_3xl: parseFloat(e.target.value) || 0 }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px 10px 25px',
                          border: '1px solid #d0d0d0',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Available Sizes */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Available Sizes
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {DEFAULT_SIZES.map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        style={{
                          padding: '8px 14px',
                          border: formData.sizes.includes(size) ? '2px solid #1a1a1a' : '1px solid #d0d0d0',
                          borderRadius: '6px',
                          background: formData.sizes.includes(size) ? '#1a1a1a' : '#fff',
                          color: formData.sizes.includes(size) ? '#fff' : '#333',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sizing Chart */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    Sizing Chart
                  </label>
                  <p style={{ fontSize: '12px', color: '#888', margin: '0 0 10px' }}>
                    Optional. Inches, typed however you like (e.g., 26 1/2). Columns follow Available Sizes. Shows on the product page under Add to Cart.
                  </p>
                  {(formData.size_chart || []).length > 0 && (
                    <div style={{ overflowX: 'auto', marginBottom: '10px' }}>
                      <table style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '4px', fontWeight: '500', color: '#555' }}>Measurement</th>
                            {formData.sizes.map(size => (
                              <th key={size} style={{ padding: '4px', fontWeight: '600' }}>{size}</th>
                            ))}
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {formData.size_chart.map((row, idx) => (
                            <tr key={idx}>
                              <td style={{ padding: '4px' }}>
                                <input
                                  type="text"
                                  value={row.label}
                                  onChange={(e) => updateChartRow(idx, { label: e.target.value })}
                                  placeholder="e.g., Body Length"
                                  style={{ width: '190px', padding: '6px 8px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px' }}
                                />
                              </td>
                              {formData.sizes.map(size => (
                                <td key={size} style={{ padding: '4px' }}>
                                  <input
                                    type="text"
                                    value={row.values?.[size] ?? ''}
                                    onChange={(e) => updateChartRow(idx, { values: { ...(row.values || {}), [size]: e.target.value } })}
                                    style={{ width: '62px', padding: '6px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', textAlign: 'center' }}
                                  />
                                </td>
                              ))}
                              <td style={{ padding: '4px' }}>
                                <button
                                  type="button"
                                  onClick={() => removeChartRow(idx)}
                                  style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '4px' }}
                                  aria-label="Remove row"
                                >
                                  <X size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {(formData.size_chart || []).length === 0 ? (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, size_chart: STARTER_CHART_ROWS.map(label => ({ label, values: {} })) }))}
                        style={{ padding: '8px 14px', border: '1px dashed #999', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}
                      >
                        + Start a chart (length, chest, sleeve)
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={addChartRow}
                        style={{ padding: '8px 14px', border: '1px dashed #999', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}
                      >
                        + Add row
                      </button>
                    )}
                    {products.some(p => p.id !== editingProduct?.id && p.size_chart?.length) && (
                      <select
                        value=""
                        onChange={(e) => copyChartFrom(e.target.value)}
                        style={{ padding: '8px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px' }}
                      >
                        <option value="">Copy chart from…</option>
                        {products
                          .filter(p => p.id !== editingProduct?.id && p.size_chart?.length)
                          .map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                      </select>
                    )}
                  </div>
                </div>

                {/* Colors */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    Colors
                  </label>
                  <p style={{ fontSize: '12px', color: '#888', margin: '0 0 10px' }}>
                    Optional. Add colors and shoppers must pick one; it shows on the order. Leave empty for single-color items.
                  </p>
                  {(formData.colors || []).map((c, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="color"
                        value={c.hex || '#cccccc'}
                        onChange={(e) => updateColor(idx, 'hex', e.target.value)}
                        style={{ width: '40px', height: '36px', padding: '2px', border: '1px solid #d0d0d0', borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={c.name}
                        onChange={(e) => updateColor(idx, 'name', e.target.value)}
                        placeholder="e.g., Clay"
                        style={{ flex: 1, padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeColor(idx)}
                        style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '4px' }}
                        aria-label="Remove color"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addColor}
                    style={{ padding: '8px 14px', border: '1px dashed #999', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}
                  >
                    + Add color
                  </button>
                </div>

                {/* Stock by Size */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    Stock by Size
                  </label>
                  <p style={{ fontSize: '12px', color: '#888', margin: '0 0 10px' }}>
                    Leave blank for unlimited. Only enter a number to track that size.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {formData.sizes.map(size => (
                      <div key={size}>
                        <label style={{ fontSize: '13px', color: '#555', display: 'block', marginBottom: '4px' }}>{size}</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.stock_by_size?.[size] ?? ''}
                          onChange={(e) => {
                            const updated = { ...formData.stock_by_size };
                            if (e.target.value === '') { delete updated[size]; } else { updated[size] = parseInt(e.target.value, 10); }
                            setFormData(prev => ({ ...prev, stock_by_size: updated }));
                          }}
                          placeholder="∞"
                          style={{ width: '100%', padding: '8px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* On Sale Toggle */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: formData.on_sale ? '12px' : 0 }}>
                    <input
                      type="checkbox"
                      id="on_sale"
                      checked={formData.on_sale}
                      onChange={(e) => setFormData(prev => ({ ...prev, on_sale: e.target.checked }))}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <label htmlFor="on_sale" style={{ fontWeight: '500' }}>
                      On Sale
                    </label>
                  </div>
                  {formData.on_sale && (
                    <div style={{ maxWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Sale Price (all sizes) *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#666'
                        }}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.sale_price}
                          onChange={(e) => setFormData(prev => ({ ...prev, sale_price: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px 10px 25px',
                            border: '1px solid #d0d0d0',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Active Toggle */}
                <div style={{
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <label htmlFor="active" style={{ fontWeight: '500' }}>
                    Active (visible on store)
                  </label>
                </div>

                {/* Submit Buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={closeModal}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    style={{ 
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Save size={18} />
                    {editingProduct ? 'Update' : 'Create'} Product
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminTees;
